import { useAuthStore } from '@/lib/session/auth-store';

/*
 * Session-isolation window listeners. Client-side defense-in-depth
 * on top of the authoritative cookie session. Two concerns, one installer:
 *
 *  - bfcache restore: a `pageshow` with `event.persisted === true` means the
 *    page came back from the back/forward cache as a frozen snapshot that may
 *    predate a logout. Force a full reload so authenticated UI is never shown
 *    from a stale snapshot.
 *  - cross-tab logout: the session is cookie-based and nothing is stored, so a
 *    tab has no way to notice a sibling tab logging out. `signalLogout()` bumps
 *    a deliberate logout key in localStorage (NOT a token); the `storage`
 *    handler in every other tab evicts local state and redirects to /login.
 *
 * This module lives in the `lib` layer, so it must not reach up into `app/`
 * (the layer rule forbids lib → app). The two `app/` boundaries it needs —
 * clearing the query cache and redirecting to /login outside React — are
 * INJECTED by the router root (`__root.tsx`) at install time, mirroring the
 * existing `router-ref` lazy indirection rather than importing `@/app/*`.
 */

/** Dependencies the installer needs from the app/boot layer, injected by the
 * router root so this `lib` module never imports `@/app/*` directly. */
export interface SessionIsolationDeps {
  /** Blanket-clear the query cache (app's queryClient.clear). */
  clearCache: () => void;
  /** Redirect to /login outside React (app's router, falling back to location). */
  redirectToLogin: () => void;
}

/** localStorage key carrying the deliberate cross-tab logout signal. */
export const LOGOUT_SIGNAL_KEY = 'tpafe:logout-signal';

/**
 * Bump the cross-tab logout signal. This is the ONLY use of localStorage in the
 * session layer and stores a monotonic timestamp, never a token or identity.
 * Writing a changed value is what fires the `storage` event in sibling tabs.
 */
export function signalLogout(): void {
  try {
    localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
  } catch {
    // localStorage can be unavailable (private mode / disabled); the server
    // session is still torn down in the acting tab, so failing to signal
    // siblings is a best-effort degradation, not an error.
  }
}

function onPageShow(event: PageTransitionEvent): void {
  // A persisted pageshow is a bfcache restore of a possibly stale snapshot.
  // Reload so guards re-run and /me (no-store) re-verifies the live session.
  if (event.persisted) {
    window.location.reload();
  }
}

function makeOnStorage(deps: SessionIsolationDeps) {
  return function onStorage(event: StorageEvent): void {
    // Only react to a genuine change of the logout key. Ignore unrelated keys
    // and clears (key === null) so this never fires spuriously.
    if (event.key !== LOGOUT_SIGNAL_KEY || event.newValue === null) return;
    useAuthStore.getState().clear();
    deps.clearCache();
    deps.redirectToLogin();
  };
}

/**
 * Install the session-isolation listeners once. Returns a teardown that removes
 * them (used by the router root's effect cleanup). The app/boot boundaries
 * (cache clear + login redirect) are injected so this module stays in the `lib`
 * layer without importing `@/app/*`.
 */
export function installSessionIsolation(
  deps: SessionIsolationDeps,
): () => void {
  const onStorage = makeOnStorage(deps);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('storage', onStorage);
  };
}
